import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { buildReplyPrefillDrafts, ReplyDraftEntry } from "./buildReplyPrefillDrafts.util";
import { createDefaultDraft, Draft, DraftRecipient, DraftSender } from "./inboxDraft.types";
import { resolveInboxReplySubject } from "./resolveInboxReplySubject.util";

export interface BuildInitialInboxDraftsParams {
	/** The lead's reply-agent slots (reply1/2/3), in slot order. */
	replyEntries: ReplyDraftEntry[];
	/** Whether the Chat tab prefills drafts from the reply-agent output at all. */
	prefillDrafts: boolean;
	/** How many reply slots the company plan surfaces (0, 1 or 3). */
	maxDrafts: number;
	/** The lead's conversations — used to inherit the reply's "Re: …" subject. */
	conversations: Conversation[];
	defaultFrom: DraftSender | undefined;
	defaultTo: DraftRecipient | undefined;
}

export interface InitialInboxDrafts {
	drafts: Draft[];
	/** The next free draft id (`draftIdCounter` seed) after these drafts. */
	nextDraftId: number;
}

/**
 * A single empty reply draft for the manual case. It inherits the "Re: …" subject of the lead's
 * latest email conversation so an email reply is never opened with a blank subject line (PREC-3094);
 * a LinkedIn reply or a lead with no inheritable subject stays blank.
 */
const buildManualReplyDraft = (
	conversations: Conversation[],
	defaultFrom: DraftSender | undefined,
	defaultTo: DraftRecipient | undefined
): Draft => {
	const draft = createDefaultDraft(1, defaultFrom, defaultTo);
	const subject = resolveInboxReplySubject(conversations, defaultFrom?.type);

	if (subject) {
		draft.subject = subject;
	}

	return draft;
};

/**
 * The drafts a freshly-opened inbox composer starts with when the lead changes: the reply-agent
 * slots (reply1/2/3) when reply prefill is enabled and any exist, otherwise a single empty reply
 * draft. Both inherit the incoming email's subject as "Re: …" (PREC-3094).
 */
export const buildInitialInboxDrafts = ({
	replyEntries,
	prefillDrafts,
	maxDrafts,
	conversations,
	defaultFrom,
	defaultTo,
}: BuildInitialInboxDraftsParams): InitialInboxDrafts => {
	if (prefillDrafts && maxDrafts > 0) {
		const prefilled = buildReplyPrefillDrafts(
			replyEntries.slice(0, maxDrafts),
			defaultFrom,
			defaultTo,
			conversations
		);

		if (prefilled.length > 0) {
			return { drafts: prefilled, nextDraftId: prefilled.length + 1 };
		}
	}

	return {
		drafts: [buildManualReplyDraft(conversations, defaultFrom, defaultTo)],
		nextDraftId: 2,
	};
};
